import { DeleteOutlined } from '@ant-design/icons';
import {
  Alert,
  Avatar,
  DatePicker,
  Drawer,
  message,
  Select,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import {
  ClipboardCopy,
  Clock3,
  Eye,
  FileSearch2,
  Loader2,
  Plus,
  Search,
  ShieldAlert,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { alertAttributionApi } from '@/services/alertAttributionApi';
import { authStore } from '@/stores';
import type {
  AlertAttributionReply,
  AlertType,
  AttributionField,
  AttributionKind,
  GenericAttributionEventPage,
  GenericAttributionGroup,
  GenericAttributionReply,
  HTTPFailureActorGroup,
  HTTPFailureEvent,
  HTTPFailureEventPage,
  HTTPFailureEventPageRequest,
  HTTPFailureTrendGroup,
  HTTPFailureTrendGroupBy,
  HTTPFailureTrendSeries,
  HTTPStatusClass,
  ResolvedRoom,
} from '@/types/alertAttribution';
import { buildAlertAttributionDrilldownPrompt } from './drilldownPrompt';
import {
  compileHTTPFailureFilters,
  filterValuePlaceholder,
  HTTP_FAILURE_FILTER_OPTIONS,
  HTTP_PATH_MATCH_MODE_OPTIONS,
  type HTTPFailureFilterKey,
  type HTTPFailureFilterRow,
} from './httpFailureFilters';
import {
  queryHTTPFailureSummary,
  queryHTTPFailureWithFirstPage,
} from './httpFailureQuery';
import {
  aggregateHTTPFailureTrendSeries,
  buildHTTPFailureTrendTooltipRows,
  HTTP_FAILURE_TREND_BUCKET_OPTIONS,
  type HTTPFailureTrendTooltipPayload,
} from './httpFailureTrend';
import { UserLookupWidget } from './UserLookupWidget';

const DEFAULT_WINDOW_MS = 15 * 60 * 1000;
const MAX_QUERY_WINDOW_MS = 24 * 60 * 60 * 1000;
const DEFAULT_EVENT_PAGE_SIZE = 20;

interface GenericEventQuery {
  from: string;
  to: string;
  alertUid: string;
  includeSensitive: boolean;
}

interface RunQueryOptions {
  excludeInternalUsers?: boolean;
}

function recentTimeRange(
  amount: number,
  unit: 'minute' | 'hour' | 'day',
): [Dayjs, Dayjs] {
  const end = dayjs();
  return [end.subtract(amount, unit), end];
}

const TIME_RANGE_PRESETS = [
  {
    label: '最近 30 分钟',
    value: () => recentTimeRange(30, 'minute'),
  },
  {
    label: '最近 1 小时',
    value: () => recentTimeRange(1, 'hour'),
  },
  {
    label: '最近 2 小时',
    value: () => recentTimeRange(2, 'hour'),
  },
  {
    label: '最近 3 小时',
    value: () => recentTimeRange(3, 'hour'),
  },
  {
    label: '最近 6 小时',
    value: () => recentTimeRange(6, 'hour'),
  },
  {
    label: '最近一天',
    value: () => recentTimeRange(1, 'day'),
  },
  {
    label: '昨天',
    value: () => {
      const yesterday = dayjs().subtract(1, 'day');
      return [yesterday.startOf('day'), yesterday.endOf('day')] as [
        Dayjs,
        Dayjs,
      ];
    },
  },
];

const ALERT_TYPE_GROUPS: Array<{
  kind: AttributionKind;
  label: string;
}> = [
  {
    kind: 'ATTRIBUTION_KIND_HTTP_FAILURE',
    label: 'HTTP 请求错误',
  },
  {
    kind: 'ATTRIBUTION_KIND_POLICY_HEALTH',
    label: 'DeviceLink 策略健康',
  },
  {
    kind: 'ATTRIBUTION_KIND_RELAY_DESCRIPTOR',
    label: 'Relay / Authority',
  },
  {
    kind: 'ATTRIBUTION_KIND_HTTP_LATENCY',
    label: 'HTTP 性能',
  },
  {
    kind: 'ATTRIBUTION_KIND_GRPC_FAILURE',
    label: 'gRPC 错误',
  },
  {
    kind: 'ATTRIBUTION_KIND_SCRAPE_HEALTH',
    label: '监控采集健康',
  },
  {
    kind: 'ATTRIBUTION_KIND_RATE_LIMIT',
    label: '限流',
  },
  {
    kind: 'ATTRIBUTION_KIND_PROVIDER_STABILITY',
    label: 'LLM Provider 稳定性',
  },
  {
    kind: 'ATTRIBUTION_KIND_NOTIFICATION_PIPELINE',
    label: '告警投递链路',
  },
];

function buildAlertTypeOptions(alertTypes: AlertType[]) {
  const knownKinds = new Set<AttributionKind>(
    ALERT_TYPE_GROUPS.map((group) => group.kind),
  );
  const toOption = (item: AlertType) => ({
    value: item.uid,
    label: `${item.title}${item.attributionSupported ? '' : '（暂不支持归因）'}`,
    disabled: !item.attributionSupported,
  });
  const groups = ALERT_TYPE_GROUPS.map((group) => {
    const items = alertTypes
      .filter((item) => item.attributionKind === group.kind)
      .sort((left, right) => left.title.localeCompare(right.title, 'zh-CN'));
    return {
      label: `${group.label}（${items.length}）`,
      options: items.map(toOption),
    };
  }).filter((group) => group.options.length > 0);
  const otherItems = alertTypes
    .filter((item) => !knownKinds.has(item.attributionKind))
    .sort((left, right) => left.title.localeCompare(right.title, 'zh-CN'));
  if (otherItems.length > 0) {
    groups.push({
      label: `其他告警（${otherItems.length}）`,
      options: otherItems.map(toOption),
    });
  }
  return groups;
}

function toLocalInput(value: Date | string) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 19);
}

function toISO(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

function formatDateTime(value?: string) {
  if (!value) return '源日志未提供';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('zh-CN', { hour12: false });
}

function formatBytes(value: number) {
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  const units = ['B', 'KiB', 'MiB', 'GiB'];
  let current = value;
  let index = 0;
  while (current >= 1024 && index < units.length - 1) {
    current /= 1024;
    index += 1;
  }
  return `${current.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function present(value?: string) {
  return value?.trim() || '源日志未提供';
}

function resolveLabel(status: string) {
  switch (status) {
    case 'RESOLVED':
      return <Tag color="green">当前信息已解析</Tag>;
    case 'MISSING_EVENT_ID':
      return <Tag color="orange">事件未上报 ID</Tag>;
    case 'UNAVAILABLE':
      return <Tag color="red">当前信息查询失败</Tag>;
    default:
      return <Tag>当前记录不存在</Tag>;
  }
}

function SummaryCard({
  label,
  value,
  note,
}: {
  label: string;
  value: number | string;
  note?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-white p-4 shadow-sm">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
      {note ? (
        <p className="mt-1 text-xs text-muted-foreground">{note}</p>
      ) : null}
    </div>
  );
}

function Distribution({
  title,
  items,
}: {
  title: string;
  items: { value: string; count: number }[];
}) {
  return (
    <div className="rounded-lg border border-border bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <div className="mt-3 flex flex-wrap gap-2">
        {items.slice(0, 12).map((item) => (
          <Tag key={`${title}-${item.value}`}>
            {item.value || 'UNKNOWN'} · {item.count}
          </Tag>
        ))}
        {!items.length ? (
          <span className="text-sm text-muted-foreground">暂无数据</span>
        ) : null}
      </div>
    </div>
  );
}

const HTTP_TREND_COLORS = [
  '#f97316',
  '#2563eb',
  '#dc2626',
  '#16a34a',
  '#9333ea',
  '#0891b2',
  '#ca8a04',
  '#db2777',
  '#475569',
  '#64748b',
];

const DEFAULT_HTTP_TREND_GROUP_BY: HTTPFailureTrendGroupBy =
  'HTTP_FAILURE_TREND_GROUP_BY_STATUS_CODE';

const HTTP_TREND_GROUP_LABELS: Partial<
  Record<HTTPFailureTrendGroupBy, string>
> = {
  HTTP_FAILURE_TREND_GROUP_BY_STATUS_CODE: '状态码',
  HTTP_FAILURE_TREND_GROUP_BY_ROUTE: 'Route',
  HTTP_FAILURE_TREND_GROUP_BY_REASON: '原因 / 错误',
  HTTP_FAILURE_TREND_GROUP_BY_CLIENT_VERSION: '客户端版本',
  HTTP_FAILURE_TREND_GROUP_BY_USER_ID: '用户',
  HTTP_FAILURE_TREND_GROUP_BY_DEVICE_ID: '设备',
  HTTP_FAILURE_TREND_GROUP_BY_ROOM_ID: '房间',
};

function trendLegendLabel(value: string) {
  const characters = [...value];
  return characters.length > 28
    ? `${characters.slice(0, 27).join('')}…`
    : value;
}

function HTTPFailureTrendTooltip({
  active,
  label,
  payload,
  series,
  groupLabel,
}: {
  active?: boolean;
  label?: string | number;
  payload?: HTTPFailureTrendTooltipPayload[];
  series: HTTPFailureTrendSeries[];
  groupLabel: string;
}) {
  if (!active || !payload?.length) return null;
  const { total, clusters } = buildHTTPFailureTrendTooltipRows(payload, series);

  return (
    <div className="max-w-md rounded-md border border-border bg-white p-3 shadow-lg">
      <p className="text-sm font-semibold">
        {dayjs(String(label)).format('YYYY-MM-DD HH:mm:ss')}
      </p>
      <div className="mt-2 flex items-center justify-between gap-6 text-sm">
        <span className="text-muted-foreground">全部异常</span>
        <span className="font-semibold">{total}</span>
      </div>
      <div className="my-2 border-t border-border" />
      <p className="text-xs font-medium text-muted-foreground">
        按{groupLabel}聚类
      </p>
      {clusters.length > 0 ? (
        <div className="mt-2 max-h-72 space-y-2 overflow-y-auto">
          {clusters.map((item) => (
            <div
              className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-start gap-3 text-sm"
              key={item.key}
            >
              <span className="flex min-w-0 items-start gap-2">
                <span
                  className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="break-all">{item.label}</span>
              </span>
              <span className="font-medium">{item.count}</span>
              <span className="w-14 text-right text-muted-foreground">
                {item.percentage.toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">
          该时间桶没有匹配异常
        </p>
      )}
    </div>
  );
}

function HTTPFailureTrend({
  groups,
  legacySeries,
  baseBucketSeconds,
  from,
  to,
}: {
  groups: HTTPFailureTrendGroup[];
  legacySeries: HTTPFailureTrendSeries[];
  baseBucketSeconds: number;
  from: string;
  to: string;
}) {
  const availableGroups = useMemo(() => {
    const configuredGroups = groups.filter(
      (group) => group.groupBy !== 'HTTP_FAILURE_TREND_GROUP_BY_UNSPECIFIED',
    );
    if (configuredGroups.length > 0) return configuredGroups;
    return legacySeries.length > 0
      ? [
          {
            groupBy: DEFAULT_HTTP_TREND_GROUP_BY,
            series: legacySeries,
          },
        ]
      : [];
  }, [groups, legacySeries]);
  const [selectedGroupBy, setSelectedGroupBy] =
    useState<HTTPFailureTrendGroupBy>(DEFAULT_HTTP_TREND_GROUP_BY);
  const [selectedBucketSeconds, setSelectedBucketSeconds] = useState(60);
  const selectedGroup =
    availableGroups.find((group) => group.groupBy === selectedGroupBy) ??
    availableGroups[0];
  const bucketOptions = useMemo(
    () =>
      HTTP_FAILURE_TREND_BUCKET_OPTIONS.filter(
        (option) => option.value >= Math.max(baseBucketSeconds, 60),
      ),
    [baseBucketSeconds],
  );
  const effectiveBucketSeconds =
    bucketOptions.find((option) => option.value === selectedBucketSeconds)
      ?.value ??
    bucketOptions[0]?.value ??
    Math.max(baseBucketSeconds, 60);
  const series = useMemo(
    () =>
      aggregateHTTPFailureTrendSeries(
        selectedGroup?.series ?? [],
        from,
        to,
        effectiveBucketSeconds,
      ),
    [effectiveBucketSeconds, from, selectedGroup?.series, to],
  );
  const groupLabel =
    HTTP_TREND_GROUP_LABELS[selectedGroup?.groupBy] ?? '当前维度';
  const chartData = useMemo(() => {
    const rows = new Map<string, Record<string, string | number>>();
    for (const item of series) {
      for (const point of item.points) {
        const row = rows.get(point.timestamp) ?? {
          timestamp: point.timestamp,
        };
        row[item.key] = point.count;
        rows.set(point.timestamp, row);
      }
    }
    return [...rows.values()].sort((left, right) =>
      String(left.timestamp).localeCompare(String(right.timestamp)),
    );
  }, [series]);
  const total = series.find((item) => item.key === 'total');
  const totalPoints = total?.points ?? [];
  const peak = totalPoints.reduce(
    (current, point) => (point.count > current.count ? point : current),
    { timestamp: '', count: 0 },
  );
  const average =
    totalPoints.reduce((sum, point) => sum + point.count, 0) /
    Math.max(totalPoints.length, 1);

  return (
    <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="text-base font-semibold">接口异常趋势</h2>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <p className="mb-1 text-xs text-muted-foreground">聚类维度</p>
            <Select
              aria-label="趋势聚类维度"
              className="w-40"
              value={selectedGroup?.groupBy}
              options={availableGroups.map((group) => ({
                label: HTTP_TREND_GROUP_LABELS[group.groupBy] ?? group.groupBy,
                value: group.groupBy,
              }))}
              onChange={setSelectedGroupBy}
            />
          </div>
          <div>
            <p className="mb-1 text-xs text-muted-foreground">统计粒度</p>
            <Select
              aria-label="趋势统计粒度"
              className="w-32"
              value={effectiveBucketSeconds}
              options={bucketOptions}
              onChange={setSelectedBucketSeconds}
            />
          </div>
          <div className="flex flex-wrap gap-2 pb-1">
            <Tag>平均 {average.toFixed(1)} / 桶</Tag>
            <Tag color="orange">
              峰值 {peak.count}
              {peak.timestamp
                ? ` · ${dayjs(peak.timestamp).format('MM-DD HH:mm')}`
                : ''}
            </Tag>
          </div>
        </div>
      </div>
      <div className="mt-4 h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="timestamp"
              minTickGap={28}
              tickFormatter={(value) => dayjs(value).format('HH:mm')}
            />
            <YAxis allowDecimals={false} width={52} />
            <RechartsTooltip
              content={
                <HTTPFailureTrendTooltip
                  groupLabel={groupLabel}
                  series={series}
                />
              }
            />
            <Legend
              formatter={(value) => (
                <span title={String(value)}>
                  {trendLegendLabel(String(value))}
                </span>
              )}
            />
            {series.map((item, index) => (
              <Line
                dataKey={item.key}
                dot={false}
                isAnimationActive={false}
                key={item.key}
                name={item.label}
                stroke={HTTP_TREND_COLORS[index % HTTP_TREND_COLORS.length]}
                strokeWidth={item.key === 'total' ? 3 : 1.5}
                type="linear"
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function AttributionFieldList({ fields }: { fields: AttributionField[] }) {
  if (!fields.length) {
    return <span className="text-muted-foreground">N/A</span>;
  }
  return (
    <div className="space-y-1">
      {fields.map((field) => (
        <div
          key={`${field.key}-${field.value}`}
          className="break-all font-mono text-xs"
        >
          <span className="text-muted-foreground">{field.key}:</span>{' '}
          {field.value || 'UNKNOWN'}
        </div>
      ))}
    </div>
  );
}

interface NotificationPipelineStageRow {
  key: string;
  count: number;
  alertName: string;
  sourceType: string;
  stage: string;
  result: string;
  averageMs: number;
  p50Ms: number;
  p95Ms: number;
  maxMs: number;
}

const NOTIFICATION_STAGE_LABELS: Record<string, string> = {
  logs_insights_enrichment: '日志归因查询',
  feishu_delivery: '飞书消息投递',
  total: '单条告警总耗时',
};

function fieldValue(fields: AttributionField[], key: string) {
  return fields.find((field) => field.key === key)?.value || '';
}

function numberField(fields: AttributionField[], key: string) {
  const value = Number(fieldValue(fields, key));
  return Number.isFinite(value) ? value : 0;
}

function notificationPipelineStageRows(
  groups: GenericAttributionGroup[],
): NotificationPipelineStageRow[] {
  return groups
    .map((group, index) => ({
      key: `${group.summary}-${index}`,
      count: group.count,
      alertName: fieldValue(group.identifiers, 'alertName'),
      sourceType: fieldValue(group.identifiers, 'sourceType'),
      stage: fieldValue(group.identifiers, 'stage'),
      result: fieldValue(group.identifiers, 'result'),
      averageMs: numberField(group.evidence, 'durationAvgMs'),
      p50Ms: numberField(group.evidence, 'durationP50Ms'),
      p95Ms: numberField(group.evidence, 'durationP95Ms'),
      maxMs: numberField(group.evidence, 'durationMaxMs'),
    }))
    .sort((left, right) => right.p95Ms - left.p95Ms);
}

function DetailItem({
  label,
  value,
  mono = true,
}: {
  label: string;
  value?: string;
  mono?: boolean;
}) {
  return (
    <div className="rounded-md border border-border bg-slate-50 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={`mt-1 break-all text-sm text-foreground ${
          mono ? 'font-mono' : ''
        }`}
      >
        {present(value)}
      </p>
    </div>
  );
}

export function AlertAttributionPage({
  mode = 'alert-attribution',
}: {
  mode?: 'alert-attribution' | 'http-exception-query';
}) {
  const isHTTPExceptionQuery = mode === 'http-exception-query';
  const [searchParams, setSearchParams] = useSearchParams();
  const now = useMemo(() => new Date(), []);
  const initialTo = searchParams.get('to') || now.toISOString();
  const initialFrom =
    searchParams.get('from') ||
    new Date(new Date(initialTo).getTime() - DEFAULT_WINDOW_MS).toISOString();
  const [from, setFrom] = useState(toLocalInput(initialFrom));
  const [to, setTo] = useState(toLocalInput(initialTo));
  const [statusClass, setStatusClass] = useState<HTTPStatusClass>(
    searchParams.get('statusClass') === '5xx'
      ? '5xx'
      : searchParams.get('statusClass') === '4xx'
        ? '4xx'
        : isHTTPExceptionQuery
          ? 'all'
          : '4xx',
  );
  const [filterRows, setFilterRows] = useState<HTTPFailureFilterRow[]>(
    isHTTPExceptionQuery
      ? [
          { id: 1, key: 'statusCodes', values: [] },
          {
            id: 2,
            key: 'requestPaths',
            values: [],
            matchMode: 'exact',
          },
        ]
      : [],
  );
  const nextFilterRowID = useRef(isHTTPExceptionQuery ? 3 : 1);
  const [includeSensitive, setIncludeSensitive] = useState(false);
  const [excludeInternalUsers, setExcludeInternalUsers] = useState(
    ['1', 'true'].includes(
      (searchParams.get('excludeInternalUsers') || '').toLowerCase(),
    ),
  );
  const [result, setResult] = useState<AlertAttributionReply | null>(null);
  const [eventPage, setEventPage] = useState<HTTPFailureEventPage | null>(null);
  const [eventPageNumber, setEventPageNumber] = useState(1);
  const [eventPageSize, setEventPageSize] = useState(DEFAULT_EVENT_PAGE_SIZE);
  const [eventPageTokens, setEventPageTokens] = useState<string[]>(['']);
  const [httpEventPageCache, setHTTPEventPageCache] = useState<
    Record<number, HTTPFailureEventPage>
  >({});
  const [genericEventPageCache, setGenericEventPageCache] = useState<
    Record<number, GenericAttributionEventPage>
  >({});
  const [eventPageLoading, setEventPageLoading] = useState(false);
  const [genericResult, setGenericResult] =
    useState<GenericAttributionReply | null>(null);
  const [genericEventPage, setGenericEventPage] =
    useState<GenericAttributionEventPage | null>(null);
  const [alertTypes, setAlertTypes] = useState<AlertType[]>([]);
  const [catalogWarnings, setCatalogWarnings] = useState<string[]>([]);
  const [selectedAlertUid, setSelectedAlertUid] = useState(
    searchParams.get('alertUid') || '',
  );
  const [catalogLoading, setCatalogLoading] = useState(!isHTTPExceptionQuery);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [noticeCycle, setNoticeCycle] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState<HTTPFailureEvent | null>(
    null,
  );
  const [drilldownPrompt, setDrilldownPrompt] = useState('');
  const [promptDrawerOpen, setPromptDrawerOpen] = useState(false);
  const autoQueried = useRef(false);
  const autoCopyPromptHandled = useRef(false);
  const eventQueryRef = useRef<HTTPFailureEventPageRequest | null>(null);
  const genericEventQueryRef = useRef<GenericEventQuery | null>(null);
  const autoCopyPromptRequested = searchParams.get('action') === 'copyPrompt';
  const canRead = authStore.can(ADMIN_PERMISSIONS.alertAttributionRead);
  const canReadSensitive = authStore.can(
    ADMIN_PERMISSIONS.alertAttributionSensitive,
  );
  const selectedAlertType = alertTypes.find(
    (item) => item.uid === selectedAlertUid,
  );
  const timeRangeValue = useMemo(() => {
    const start = dayjs(from);
    const end = dayjs(to);
    return start.isValid() && end.isValid()
      ? ([start, end] as [Dayjs, Dayjs])
      : null;
  }, [from, to]);
  const alertTypeOptions = useMemo(
    () => buildAlertTypeOptions(alertTypes),
    [alertTypes],
  );
  const notificationStageRows = useMemo(
    () =>
      genericResult?.alertType.attributionKind ===
      'ATTRIBUTION_KIND_NOTIFICATION_PIPELINE'
        ? notificationPipelineStageRows(genericResult.groups)
        : [],
    [genericResult],
  );
  const notificationBottleneck = useMemo(
    () =>
      notificationStageRows.find((row) => row.stage !== 'total') ??
      notificationStageRows[0],
    [notificationStageRows],
  );
  const isHTTPFailure =
    isHTTPExceptionQuery ||
    selectedAlertType?.attributionKind === 'ATTRIBUTION_KIND_HTTP_FAILURE';
  const attributionWarnings = useMemo(
    () =>
      [
        ...catalogWarnings,
        ...(result?.warnings ?? []),
        ...(genericResult?.warnings ?? []),
        ...(eventPage?.warnings ?? []),
        ...(genericEventPage?.warnings ?? []),
      ].filter(
        (warning, index, warnings) =>
          warning.trim() !== '' && warnings.indexOf(warning) === index,
      ),
    [
      catalogWarnings,
      eventPage?.warnings,
      genericEventPage?.warnings,
      genericResult?.warnings,
      result?.warnings,
    ],
  );

  useEffect(() => {
    if (isHTTPExceptionQuery) {
      setCatalogLoading(false);
      setCatalogWarnings([]);
      return;
    }
    let active = true;
    setCatalogLoading(true);
    alertAttributionApi
      .listAlertTypes()
      .then((catalog) => {
        if (!active) return;
        const items = catalog.alertTypes;
        setAlertTypes(items);
        setCatalogWarnings(catalog.warnings);
        setSelectedAlertUid((current) => {
          if (current && items.some((item) => item.uid === current)) {
            return current;
          }
          const requestedStatus = searchParams.get('statusClass') || '4xx';
          return (
            items.find(
              (item) =>
                item.attributionKind === 'ATTRIBUTION_KIND_HTTP_FAILURE' &&
                item.httpStatusClass.toLowerCase().includes(requestedStatus),
            )?.uid ||
            items.find((item) => item.attributionSupported)?.uid ||
            items[0]?.uid ||
            ''
          );
        });
      })
      .catch((catalogError: { message?: string }) => {
        if (active) {
          setError(catalogError.message || '线上告警类型加载失败');
        }
      })
      .finally(() => {
        if (active) setCatalogLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isHTTPExceptionQuery, searchParams]);

  const runQuery = useCallback(
    async (options?: RunQueryOptions) => {
      if (!canRead) {
        setError('当前账号缺少告警归因读取权限');
        return;
      }
      const fromISO = toISO(from);
      const toISOValue = toISO(to);
      if (!fromISO || !toISOValue) {
        message.error('请选择有效的查询时间');
        return;
      }
      if (!isHTTPExceptionQuery && !selectedAlertType) {
        setError('请选择一个线上告警类型');
        return;
      }
      const compiledFilters = compileHTTPFailureFilters(filterRows);
      if (compiledFilters.error) {
        message.error(compiledFilters.error);
        return;
      }
      const shouldExcludeInternalUsers =
        options?.excludeInternalUsers ?? excludeInternalUsers;
      const effectiveStatusClass: HTTPStatusClass =
        isHTTPExceptionQuery || !selectedAlertType
          ? statusClass
          : selectedAlertType.httpStatusClass.toLowerCase().includes('5xx')
            ? '5xx'
            : '4xx';
      setNoticeCycle((current) => current + 1);
      setLoading(true);
      setError('');
      try {
        if (isHTTPFailure) {
          setStatusClass(effectiveStatusClass);
          const queryRequest: HTTPFailureEventPageRequest = {
            from: fromISO,
            to: toISOValue,
            statusClass: effectiveStatusClass,
            filters: compiledFilters.filters,
            includeSensitive: includeSensitive && canReadSensitive,
            excludeInternalUsers: shouldExcludeInternalUsers,
          };
          eventQueryRef.current = queryRequest;
          genericEventQueryRef.current = null;
          if (isHTTPExceptionQuery) {
            const next = await queryHTTPFailureSummary(
              alertAttributionApi,
              queryRequest,
            );
            setResult(next);
            setEventPage(null);
            setGenericEventPage(null);
            setHTTPEventPageCache({});
            setGenericEventPageCache({});
            setEventPageNumber(1);
            setEventPageTokens([next.firstPageToken]);
          } else {
            const { summary: next, firstPage: firstEventPage } =
              await queryHTTPFailureWithFirstPage(
                alertAttributionApi,
                queryRequest,
                eventPageSize,
              );
            setResult(next);
            setEventPage(firstEventPage);
            setGenericEventPage(null);
            setHTTPEventPageCache({ 1: firstEventPage });
            setGenericEventPageCache({});
            setEventPageNumber(1);
            setEventPageTokens([
              next.firstPageToken,
              ...(firstEventPage.nextPageToken
                ? [firstEventPage.nextPageToken]
                : []),
            ]);
          }
          setGenericResult(null);
        } else if (selectedAlertType) {
          const genericQuery: GenericEventQuery = {
            from: fromISO,
            to: toISOValue,
            alertUid: selectedAlertType.uid,
            includeSensitive: includeSensitive && canReadSensitive,
          };
          genericEventQueryRef.current = genericQuery;
          const [next, firstEventPage] = await Promise.all([
            alertAttributionApi.queryGeneric({
              ...genericQuery,
              detailLimit: 1,
              summaryOnly: true,
            }),
            alertAttributionApi.queryGenericEvents({
              ...genericQuery,
              pageSize: eventPageSize,
            }),
          ]);
          setGenericResult(next);
          setGenericEventPage(firstEventPage);
          setGenericEventPageCache({ 1: firstEventPage });
          setHTTPEventPageCache({});
          setEventPageNumber(1);
          setEventPageTokens([
            '',
            ...(firstEventPage.nextPageToken
              ? [firstEventPage.nextPageToken]
              : []),
          ]);
          setResult(null);
          setEventPage(null);
          eventQueryRef.current = null;
        }
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('from', fromISO);
        nextParams.set('to', toISOValue);
        if (selectedAlertType) {
          nextParams.set('alertUid', selectedAlertType.uid);
        } else {
          nextParams.delete('alertUid');
        }
        if (isHTTPFailure) {
          nextParams.set('statusClass', effectiveStatusClass);
          if (shouldExcludeInternalUsers) {
            nextParams.set('excludeInternalUsers', 'true');
          } else {
            nextParams.delete('excludeInternalUsers');
          }
        }
        setSearchParams(nextParams, { replace: true });
      } catch (queryError) {
        const fallback = '告警归因查询失败';
        const candidate = queryError as {
          message?: string;
          response?: { data?: { message?: string; error?: string } };
        };
        setError(
          candidate.response?.data?.message ||
            candidate.response?.data?.error ||
            candidate.message ||
            fallback,
        );
      } finally {
        setLoading(false);
      }
    },
    [
      canRead,
      canReadSensitive,
      filterRows,
      from,
      includeSensitive,
      isHTTPExceptionQuery,
      isHTTPFailure,
      searchParams,
      selectedAlertType,
      setSearchParams,
      statusClass,
      to,
      eventPageSize,
      excludeInternalUsers,
    ],
  );

  const loadHTTPEventPage = useCallback(
    async (pageNumber: number, pageSize: number) => {
      const query = eventQueryRef.current;
      if (!query) return;
      if (pageSize === eventPageSize && httpEventPageCache[pageNumber]) {
        setEventPage(httpEventPageCache[pageNumber]);
        setEventPageNumber(pageNumber);
        setSelectedEvent(null);
        return;
      }
      const pageToken = eventPageTokens[pageNumber - 1];
      if (pageNumber > 1 && !pageToken) {
        message.warning('该页游标尚未生成，请按顺序翻页');
        return;
      }
      setEventPageLoading(true);
      try {
        const next = await alertAttributionApi.queryHTTPFailureEvents({
          ...query,
          pageSize,
          pageToken,
        });
        setEventPage(next);
        setEventPageNumber(pageNumber);
        setEventPageSize(pageSize);
        setSelectedEvent(null);
        setHTTPEventPageCache((current) =>
          pageSize === eventPageSize
            ? { ...current, [pageNumber]: next }
            : { 1: next },
        );
        setEventPageTokens((current) => {
          const tokens =
            pageNumber === 1 || pageSize !== eventPageSize
              ? [current[0]]
              : current.slice(0, pageNumber);
          if (next.nextPageToken) {
            tokens[pageNumber] = next.nextPageToken;
          }
          return tokens;
        });
      } catch (pageError) {
        const candidate = pageError as {
          message?: string;
          response?: { data?: { message?: string; error?: string } };
        };
        message.error(
          candidate.response?.data?.message ||
            candidate.response?.data?.error ||
            candidate.message ||
            '事件明细翻页失败',
        );
      } finally {
        setEventPageLoading(false);
      }
    },
    [eventPageSize, eventPageTokens, httpEventPageCache],
  );

  const loadGenericEventPage = useCallback(
    async (pageNumber: number, pageSize: number) => {
      const query = genericEventQueryRef.current;
      if (!query) return;
      if (pageSize === eventPageSize && genericEventPageCache[pageNumber]) {
        setGenericEventPage(genericEventPageCache[pageNumber]);
        setEventPageNumber(pageNumber);
        return;
      }
      const pageToken = pageNumber === 1 ? '' : eventPageTokens[pageNumber - 1];
      if (pageNumber > 1 && !pageToken) {
        message.warning('该页游标尚未生成，请按顺序翻页');
        return;
      }
      setEventPageLoading(true);
      try {
        const next = await alertAttributionApi.queryGenericEvents({
          ...query,
          pageSize,
          pageToken,
        });
        setGenericEventPage(next);
        setEventPageNumber(pageNumber);
        setEventPageSize(pageSize);
        setGenericEventPageCache((current) =>
          pageSize === eventPageSize
            ? { ...current, [pageNumber]: next }
            : { 1: next },
        );
        setEventPageTokens((current) => {
          const tokens =
            pageNumber === 1 || pageSize !== eventPageSize
              ? ['']
              : current.slice(0, pageNumber);
          if (next.nextPageToken) {
            tokens[pageNumber] = next.nextPageToken;
          }
          return tokens;
        });
      } catch (pageError) {
        const candidate = pageError as {
          message?: string;
          response?: { data?: { message?: string; error?: string } };
        };
        message.error(
          candidate.response?.data?.message ||
            candidate.response?.data?.error ||
            candidate.message ||
            '事件证据翻页失败',
        );
      } finally {
        setEventPageLoading(false);
      }
    },
    [eventPageSize, eventPageTokens, genericEventPageCache],
  );

  useEffect(() => {
    if (autoQueried.current) return;
    if (catalogLoading || (!isHTTPExceptionQuery && !selectedAlertType)) return;
    autoQueried.current = true;
    if (canRead) void runQuery();
  }, [
    canRead,
    catalogLoading,
    isHTTPExceptionQuery,
    runQuery,
    selectedAlertType,
  ]);

  const generateAndCopyDrilldownPrompt = useCallback(async () => {
    const fromISO = toISO(from);
    const toISOValue = toISO(to);
    if (!fromISO || !toISOValue) {
      message.error('请选择有效的查询时间');
      return;
    }
    const compiledFilters = compileHTTPFailureFilters(filterRows);
    if (compiledFilters.error) {
      message.error(compiledFilters.error);
      return;
    }
    const prompt = buildAlertAttributionDrilldownPrompt({
      from: fromISO,
      to: toISOValue,
      statusClass,
      alertName: searchParams.get('alertName') || undefined,
      fingerprint: searchParams.get('fingerprint') || undefined,
      statusCodes: compiledFilters.filters.statusCodes,
      filters: compiledFilters.filters,
      excludeInternalUsers:
        result?.internalUserFilter.enabled ?? excludeInternalUsers,
      result:
        result && eventPage
          ? {
              ...result,
              events: eventPage.events,
            }
          : result,
      alertType: selectedAlertType,
      genericResult:
        genericResult && genericEventPage
          ? {
              ...genericResult,
              events: genericEventPage.events,
            }
          : genericResult,
    });
    setDrilldownPrompt(prompt);
    setPromptDrawerOpen(true);
    try {
      await navigator.clipboard.writeText(prompt);
      message.success('下钻 Prompt 已生成并复制');
    } catch {
      message.warning('Prompt 已生成，自动复制失败，请在抽屉中手动复制');
    }
  }, [
    filterRows,
    from,
    genericResult,
    genericEventPage,
    result,
    eventPage,
    excludeInternalUsers,
    searchParams,
    selectedAlertType,
    statusClass,
    to,
  ]);

  const copyDrilldownPrompt = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(drilldownPrompt);
      message.success('下钻 Prompt 已复制');
    } catch {
      message.error('复制失败，请手动选择 Prompt 内容');
    }
  }, [drilldownPrompt]);

  useEffect(() => {
    if (
      !autoCopyPromptRequested ||
      autoCopyPromptHandled.current ||
      (!result && !genericResult)
    ) {
      return;
    }
    autoCopyPromptHandled.current = true;
    void generateAndCopyDrilldownPrompt();
  }, [
    autoCopyPromptRequested,
    generateAndCopyDrilldownPrompt,
    genericResult,
    result,
  ]);

  const actorColumns: ColumnsType<HTTPFailureActorGroup> = [
    {
      title: '次数',
      dataIndex: 'count',
      width: 80,
      sorter: (a, b) => a.count - b.count,
      defaultSortOrder: 'descend',
    },
    {
      title: '当前用户信息',
      key: 'user',
      width: 260,
      render: (_, row) => (
        <div className="min-w-0 space-y-1">
          <div className="flex min-w-0 items-center gap-2">
            <Avatar
              className="shrink-0"
              src={row.user.avatar || undefined}
              size={32}
            >
              {(row.user.name || row.user.userId).slice(0, 1)}
            </Avatar>
            <Tooltip title={row.user.name || '名称未解析'}>
              <span className="block w-0 min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-medium">
                {row.user.name || '名称未解析'}
              </span>
            </Tooltip>
          </div>
          <div className="break-all font-mono text-xs">{row.user.userId}</div>
          {row.user.email ? (
            <div className="break-all text-xs text-muted-foreground">
              {row.user.email}
            </div>
          ) : null}
          {resolveLabel(row.user.resolveStatus)}
        </div>
      ),
    },
    {
      title: '当前设备信息 / 事件版本',
      key: 'device',
      width: 340,
      render: (_, row) => (
        <div className="space-y-1">
          <div className="font-medium">
            {row.device.displayName ||
              row.device.reportedName ||
              '设备名称未提供'}
          </div>
          <div className="break-all font-mono text-xs">
            {row.device.deviceId}
          </div>
          <div className="text-xs text-muted-foreground">
            {[row.device.platform, row.device.arch, row.device.clientVersion]
              .filter(Boolean)
              .join(' / ') || '当前设备环境未解析'}
          </div>
          <div className="text-xs text-muted-foreground">
            事件版本：
            {row.eventClientVersions.length
              ? row.eventClientVersions.join('、')
              : '源日志未提供'}
          </div>
          <div className="text-xs text-muted-foreground">
            当前首次 / 最近活跃：
            {formatDateTime(row.device.firstSeenAt)} /{' '}
            {formatDateTime(row.device.lastSeenAt)}
          </div>
          {row.device.resolveStatus === 'RESOLVED' ? (
            <Tag color={row.device.identityRegistered ? 'green' : 'default'}>
              {row.device.identityRegistered ? '身份已注册' : '身份未注册'}
            </Tag>
          ) : null}
          {resolveLabel(row.device.resolveStatus)}
          {row.candidateDevices.length ? (
            <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              <div className="font-medium">
                无法确认具体触发设备，以下仅为该用户近期候选设备：
              </div>
              {row.candidateDevices.map((candidate) => (
                <div key={candidate.userDeviceId} className="mt-1">
                  {candidate.displayName || candidate.reportedName || '未命名'}{' '}
                  · {candidate.deviceId} ·{' '}
                  {candidate.clientVersion || '版本未知'} · 最近活跃{' '}
                  {formatDateTime(candidate.lastSeenAt)}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: '主要房间',
      key: 'room',
      width: 220,
      render: (_, row) => (
        <div>
          <div className="break-all font-mono text-xs">{row.topRoomId}</div>
          <div className="text-xs text-muted-foreground">
            {row.topRoomCount} 次
          </div>
        </div>
      ),
    },
    {
      title: '主要问题',
      key: 'issue',
      render: (_, row) => (
        <div>
          <div className="break-all font-mono text-xs">{row.topIssue}</div>
          <div className="text-xs text-muted-foreground">
            {row.topIssueCount} 次
          </div>
        </div>
      ),
    },
  ];

  const eventColumns: ColumnsType<HTTPFailureEvent> = [
    {
      title: '事件时间',
      dataIndex: 'timestamp',
      width: 180,
      render: (value: string) => formatDateTime(value),
    },
    {
      title: '状态',
      dataIndex: 'statusCode',
      width: 80,
      render: (value: number) => (
        <Tag color={value >= 500 ? 'red' : 'orange'}>{value}</Tag>
      ),
    },
    {
      title: '请求',
      key: 'request',
      width: 360,
      render: (_, row) => (
        <div>
          <div className="font-mono text-xs">
            {row.method || '-'} {row.urlPath || row.route || 'UNKNOWN'}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {row.reason || 'reason 未提供'}
          </div>
        </div>
      ),
    },
    {
      title: '用户 / 设备 / 房间',
      key: 'identity',
      render: (_, row) => (
        <div className="space-y-0.5 font-mono text-xs">
          <div>user: {row.userId}</div>
          <div>device: {row.deviceId}</div>
          <div>room: {row.roomId}</div>
        </div>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 80,
      render: (_, row) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSelectedEvent(row)}
        >
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  const selectedActor = selectedEvent
    ? (eventPage?.actors.find(
        (item) =>
          item.user.userId === selectedEvent.userId &&
          item.device.deviceId === selectedEvent.deviceId,
      ) ??
      result?.actors.find(
        (item) =>
          item.user.userId === selectedEvent.userId &&
          item.device.deviceId === selectedEvent.deviceId,
      ))
    : undefined;
  const selectedRoom = selectedEvent
    ? (eventPage?.rooms.find((item) => item.roomId === selectedEvent.roomId) ??
      result?.rooms.find((item) => item.roomId === selectedEvent.roomId))
    : undefined;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-red-500" />
              <h1 className="text-lg font-semibold text-foreground">
                {isHTTPExceptionQuery ? '接口异常查询' : '告警归因'}
              </h1>
            </div>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              {isHTTPExceptionQuery
                ? '无需选择告警类型，直接按时间、HTTP 状态、接口路径及用户、设备、房间、Reason、客户端版本等维度查询接口异常。接口路径支持精确、包含和 RE2 正则匹配。'
                : '告警类型实时读取线上 Grafana 和 CloudWatch Alarm；指标维度来自 AMP 或 CloudWatch Metrics，事件字段来自固定时间窗内的 CloudWatch 日志。UNKNOWN 只表示证据缺失，不会自动归因为低版本客户端；N/A 表示该告警不适用该字段。'}
            </p>
            {!isHTTPExceptionQuery && searchParams.get('alertName') ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <Tag color="red">{searchParams.get('alertName')}</Tag>
                {searchParams.get('fingerprint') ? (
                  <Tag>{searchParams.get('fingerprint')}</Tag>
                ) : null}
              </div>
            ) : null}
            {!isHTTPExceptionQuery && selectedAlertType ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <Tag
                  color={
                    selectedAlertType.source === 'ALERT_SOURCE_CLOUDWATCH'
                      ? 'blue'
                      : 'purple'
                  }
                >
                  {selectedAlertType.source === 'ALERT_SOURCE_CLOUDWATCH'
                    ? 'CloudWatch Alarm'
                    : 'Grafana'}
                </Tag>
                {selectedAlertType.region ? (
                  <Tag>{selectedAlertType.region}</Tag>
                ) : null}
                {selectedAlertType.service ? (
                  <Tag>{selectedAlertType.service}</Tag>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="flex flex-col items-start gap-3 lg:items-end">
            <div className="flex items-center gap-2">
              <Switch
                checked={includeSensitive}
                disabled={!canReadSensitive}
                onChange={setIncludeSensitive}
              />
              <span className="text-sm text-muted-foreground">
                查看完整邮箱、IP、User-Agent
              </span>
            </div>
            <Button
              variant="outline"
              onClick={() => void generateAndCopyDrilldownPrompt()}
            >
              <FileSearch2 className="mr-2 h-4 w-4" />
              生成并复制下钻 Prompt
            </Button>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-2 md:col-span-2">
            <Label>时间范围</Label>
            <DatePicker.RangePicker
              className="w-full"
              value={timeRangeValue}
              presets={TIME_RANGE_PRESETS}
              showTime
              allowClear={false}
              format="YYYY-MM-DD HH:mm:ss"
              maxDate={dayjs()}
              minDate={dayjs().subtract(30, 'day')}
              placeholder={['开始时间', '结束时间']}
              onChange={(values) => {
                if (!values?.[0] || !values[1]) return;
                if (
                  values[1].valueOf() - values[0].valueOf() >
                  MAX_QUERY_WINDOW_MS
                ) {
                  message.error('查询时间范围最长为 24 小时');
                  return;
                }
                setFrom(toLocalInput(values[0].toDate()));
                setTo(toLocalInput(values[1].toDate()));
              }}
            />
          </div>
          {isHTTPExceptionQuery ? (
            <div className="space-y-2">
              <Label>状态类别</Label>
              <Select
                className="w-full"
                value={statusClass}
                options={[
                  { value: 'all', label: '全部异常（4xx + 5xx）' },
                  { value: '4xx', label: '4xx 客户端/契约错误' },
                  { value: '5xx', label: '5xx 服务端错误' },
                ]}
                onChange={(value: HTTPStatusClass) => {
                  setStatusClass(value);
                  setResult(null);
                  setEventPage(null);
                  autoQueried.current = true;
                }}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>线上告警类型</Label>
              <Select
                className="w-full"
                loading={catalogLoading}
                value={selectedAlertUid || undefined}
                showSearch
                optionFilterProp="label"
                options={alertTypeOptions}
                onChange={(uid) => {
                  setSelectedAlertUid(uid);
                  setResult(null);
                  setGenericResult(null);
                  autoQueried.current = true;
                }}
              />
            </div>
          )}
          <div className="flex items-end">
            <Button
              className="w-full"
              disabled={loading || !canRead}
              onClick={() => void runQuery()}
            >
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Search className="mr-2 h-4 w-4" />
              )}
              {loading
                ? '查询中'
                : isHTTPExceptionQuery
                  ? '查询异常'
                  : '查询归因'}
            </Button>
          </div>
        </div>

        {isHTTPFailure ? (
          <div className="mt-4 space-y-3">
            {filterRows.map((row) => {
              const selectedKeys = new Set(
                filterRows
                  .filter((item) => item.id !== row.id)
                  .map((item) => item.key)
                  .filter(Boolean),
              );
              return (
                <div className="flex flex-col gap-2 md:flex-row" key={row.id}>
                  <Select
                    aria-label="筛选 Key"
                    className="w-full md:w-52 md:shrink-0"
                    placeholder="选择筛选 Key"
                    value={row.key}
                    options={HTTP_FAILURE_FILTER_OPTIONS.map((option) => ({
                      value: option.value,
                      label: option.label,
                      disabled: selectedKeys.has(option.value),
                    }))}
                    onChange={(key: HTTPFailureFilterKey) =>
                      setFilterRows((current) =>
                        current.map((item) =>
                          item.id === row.id
                            ? {
                                ...item,
                                key,
                                values: [],
                                matchMode:
                                  key === 'requestPaths' ? 'exact' : undefined,
                              }
                            : item,
                        ),
                      )
                    }
                  />
                  {row.key === 'requestPaths' ? (
                    <Select
                      aria-label="接口路径匹配方式"
                      className="w-full md:w-28 md:shrink-0"
                      value={row.matchMode ?? 'exact'}
                      options={HTTP_PATH_MATCH_MODE_OPTIONS}
                      onChange={(matchMode) =>
                        setFilterRows((current) =>
                          current.map((item) =>
                            item.id === row.id ? { ...item, matchMode } : item,
                          ),
                        )
                      }
                    />
                  ) : null}
                  <Select
                    aria-label="筛选值"
                    className="alert-attribution-filter-values min-w-0 flex-1"
                    disabled={!row.key}
                    maxCount={50}
                    maxTagCount="responsive"
                    mode="tags"
                    open={false}
                    placeholder={filterValuePlaceholder(row.key)}
                    tokenSeparators={[',', '，', ';', '；', ' ', '\n']}
                    value={row.values}
                    onChange={(values) =>
                      setFilterRows((current) =>
                        current.map((item) =>
                          item.id === row.id ? { ...item, values } : item,
                        ),
                      )
                    }
                  />
                  <Button
                    aria-label="移除筛选项"
                    icon={<DeleteOutlined />}
                    size="icon"
                    variant="outline"
                    onClick={() =>
                      setFilterRows((current) =>
                        current.filter((item) => item.id !== row.id),
                      )
                    }
                  />
                </div>
              );
            })}
            {filterRows.length < HTTP_FAILURE_FILTER_OPTIONS.length ? (
              <Button
                variant="outline"
                onClick={() => {
                  const id = nextFilterRowID.current;
                  nextFilterRowID.current += 1;
                  setFilterRows((current) => [...current, { id, values: [] }]);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                添加筛选项
              </Button>
            ) : null}
            {isHTTPExceptionQuery ? (
              <p className="text-xs text-muted-foreground">
                具体错误码支持一次输入多个 400–599
                错误码，使用回车、逗号、分号或空格分隔。
              </p>
            ) : null}
            <div className="flex items-center gap-2 rounded-md border border-border px-3">
              <Switch
                aria-label="过滤内部白名单用户"
                checked={excludeInternalUsers}
                disabled={loading}
                onChange={(checked) => {
                  setExcludeInternalUsers(checked);
                  void runQuery({ excludeInternalUsers: checked });
                }}
              />
              <span className="text-sm">过滤内部白名单用户</span>
            </div>
          </div>
        ) : null}
      </div>

      {error ? <Alert closable type="error" showIcon message={error} /> : null}
      {autoCopyPromptRequested &&
      canRead &&
      !result &&
      !genericResult &&
      !error ? (
        <Alert
          closable
          type="info"
          showIcon
          message="正在按告警固定时间窗生成排查 Prompt，查询完成后将自动复制。"
        />
      ) : null}
      {!canRead ? (
        <Alert
          closable
          type="warning"
          showIcon
          message="你可以查看此页面，但没有查询日志和关联实体的读取权限。"
        />
      ) : null}
      {attributionWarnings.map((warning) => (
        <Alert
          closable
          key={`${noticeCycle}-${warning}`}
          type="warning"
          showIcon
          message={warning}
        />
      ))}
      {result?.internalUserFilter.enabled ? (
        <Alert
          closable
          key={`${noticeCycle}-internal-user-filter`}
          type="info"
          showIcon
          message={`已从统计和明细中排除 ${result.internalUserFilter.excludedEvents} 条内部用户事件，涉及 ${result.internalUserFilter.excludedUsers} 个白名单用户。`}
        />
      ) : null}
      {result?.truncated ? (
        <Alert
          closable
          key={`${noticeCycle}-http-summary-truncated`}
          type="warning"
          showIcon
          message="本次聚合达到 100,000 条日志上限，统计数据可能被截断，请缩小时间窗或增加筛选条件。"
        />
      ) : null}
      {genericResult?.truncated ? (
        <Alert
          closable
          key={`${noticeCycle}-generic-summary-truncated`}
          type="warning"
          showIcon
          message="本次诊断日志达到 100,000 条上限，请缩小时间窗。"
        />
      ) : null}
      {eventPage?.sourceTruncated ? (
        <Alert
          closable
          key={`${noticeCycle}-http-events-truncated`}
          type="warning"
          showIcon
          message="事件明细达到 CloudWatch 单次查询 100,000 条上限；当前页仍可查看，但更早事件需要缩小时间窗后重查。"
        />
      ) : null}
      {genericEventPage?.sourceTruncated ? (
        <Alert
          closable
          key={`${noticeCycle}-generic-events-truncated`}
          type="warning"
          showIcon
          message="事件证据达到 CloudWatch 单次查询 100,000 条上限，更早事件需要缩小时间窗后重查。"
        />
      ) : null}

      {genericResult ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard label="归因聚类" value={genericResult.groups.length} />
            <SummaryCard
              label="事件证据"
              value={genericResult.queryStats.recordsMatched}
              note="CloudWatch 结构化诊断事件"
            />
            <SummaryCard
              label="告警类型"
              value={genericResult.alertType.severity || '未标级'}
              note={genericResult.alertType.attributionKind}
            />
            <SummaryCard
              label="查询耗时"
              value={`${genericResult.queryStats.queryDurationMs} ms`}
              note={`扫描 ${formatBytes(genericResult.queryStats.bytesScanned)}`}
            />
          </div>

          {notificationStageRows.length > 0 ? (
            <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
              <h2 className="text-base font-semibold">告警投递耗时分解</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                按实际执行阶段聚合，直接对比日志归因查询与飞书投递耗时。
              </p>
              {notificationBottleneck ? (
                <Alert
                  className="mt-4"
                  type={
                    notificationBottleneck.result === 'failure'
                      ? 'error'
                      : 'info'
                  }
                  showIcon
                  message={`主要耗时阶段：${
                    NOTIFICATION_STAGE_LABELS[notificationBottleneck.stage] ||
                    notificationBottleneck.stage
                  }，P95 ${notificationBottleneck.p95Ms.toFixed(0)} ms`}
                  description={`告警 ${notificationBottleneck.alertName || 'UNKNOWN'}；样本 ${notificationBottleneck.count}；最大耗时 ${notificationBottleneck.maxMs.toFixed(0)} ms；结果 ${notificationBottleneck.result || 'UNKNOWN'}`}
                />
              ) : null}
              <Table
                className="mt-4"
                rowKey="key"
                dataSource={notificationStageRows}
                pagination={false}
                scroll={{ x: 1000 }}
                columns={[
                  {
                    title: '阶段',
                    dataIndex: 'stage',
                    width: 170,
                    render: (value: string) =>
                      NOTIFICATION_STAGE_LABELS[value] || value || 'UNKNOWN',
                  },
                  {
                    title: '触发告警',
                    dataIndex: 'alertName',
                    width: 260,
                    ellipsis: true,
                  },
                  {
                    title: '来源',
                    dataIndex: 'sourceType',
                    width: 150,
                  },
                  {
                    title: '结果',
                    dataIndex: 'result',
                    width: 100,
                    render: (value: string) => (
                      <Tag color={value === 'success' ? 'success' : 'error'}>
                        {value || 'UNKNOWN'}
                      </Tag>
                    ),
                  },
                  { title: '样本', dataIndex: 'count', width: 80 },
                  {
                    title: '平均',
                    dataIndex: 'averageMs',
                    width: 110,
                    render: (value: number) => `${value.toFixed(0)} ms`,
                  },
                  {
                    title: 'P50',
                    dataIndex: 'p50Ms',
                    width: 110,
                    render: (value: number) => `${value.toFixed(0)} ms`,
                  },
                  {
                    title: 'P95',
                    dataIndex: 'p95Ms',
                    width: 110,
                    sorter: (left, right) => left.p95Ms - right.p95Ms,
                    defaultSortOrder: 'descend',
                    render: (value: number) => `${value.toFixed(0)} ms`,
                  },
                  {
                    title: '最大',
                    dataIndex: 'maxMs',
                    width: 110,
                    render: (value: number) => `${value.toFixed(0)} ms`,
                  },
                ]}
              />
            </div>
          ) : null}

          <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
            <h2 className="text-base font-semibold">归因聚类</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              指标维度与日志 ID 均按原值展示；不适用字段不会伪造 UNKNOWN。
            </p>
            <Table
              className="mt-4"
              rowKey={(row, index) => `${row.summary}-${index}`}
              dataSource={genericResult.groups}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              scroll={{ x: 1050 }}
              columns={[
                {
                  title: '次数/序列',
                  dataIndex: 'count',
                  width: 100,
                  sorter: (a, b) => a.count - b.count,
                  defaultSortOrder: 'descend',
                },
                {
                  title: '归因 ID',
                  dataIndex: 'identifiers',
                  width: 330,
                  render: (fields: AttributionField[]) => (
                    <AttributionFieldList fields={fields} />
                  ),
                },
                {
                  title: '关键证据',
                  dataIndex: 'evidence',
                  render: (fields: AttributionField[]) => (
                    <AttributionFieldList fields={fields} />
                  ),
                },
              ]}
            />
          </div>

          <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-accent" />
              <h2 className="text-base font-semibold">事件证据</h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              服务端按固定查询快照游标分页。
              {genericEventPage?.cursorExpiresAt
                ? ` 当前游标有效至 ${formatDateTime(genericEventPage.cursorExpiresAt)}。`
                : ''}
            </p>
            <Table
              className="mt-4"
              rowKey={(row, index) => `${row.timestamp}-${index}`}
              dataSource={genericEventPage?.events ?? []}
              loading={eventPageLoading}
              pagination={{
                current: eventPageNumber,
                pageSize: eventPageSize,
                pageSizeOptions: [20, 50, 100],
                showSizeChanger: true,
                simple: true,
                total: genericResult.queryStats.recordsMatched,
                showTotal: (total) => `共 ${total} 条事件证据`,
              }}
              onChange={(pagination) => {
                const nextPageSize =
                  pagination.pageSize ?? DEFAULT_EVENT_PAGE_SIZE;
                const nextPage =
                  nextPageSize === eventPageSize
                    ? (pagination.current ?? 1)
                    : 1;
                if (
                  nextPage !== eventPageNumber ||
                  nextPageSize !== eventPageSize
                ) {
                  void loadGenericEventPage(nextPage, nextPageSize);
                }
              }}
              scroll={{ x: 1100 }}
              columns={[
                {
                  title: '时间',
                  dataIndex: 'timestamp',
                  width: 190,
                  render: formatDateTime,
                },
                { title: '来源', dataIndex: 'source', width: 130 },
                {
                  title: '归因 ID',
                  dataIndex: 'identifiers',
                  width: 330,
                  render: (fields: AttributionField[]) => (
                    <AttributionFieldList fields={fields} />
                  ),
                },
                {
                  title: '证据',
                  dataIndex: 'evidence',
                  render: (fields: AttributionField[]) => (
                    <AttributionFieldList fields={fields} />
                  ),
                },
              ]}
            />
          </div>
        </>
      ) : null}

      {result ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
            <SummaryCard label="错误总数" value={result.summary.total} />
            <SummaryCard label="用户数" value={result.summary.uniqueUsers} />
            <SummaryCard label="设备数" value={result.summary.uniqueDevices} />
            <SummaryCard label="房间数" value={result.summary.uniqueRooms} />
            <SummaryCard
              label="信息缺失率"
              value={`${(
                ((result.missingFields.userId +
                  result.missingFields.deviceId +
                  result.missingFields.roomId +
                  result.missingFields.clientVersion) /
                  Math.max(result.summary.total * 4, 1)) *
                  100
              ).toFixed(1)}%`}
              note={`用户 ${result.missingFields.userId} / 设备 ${result.missingFields.deviceId} / 房间 ${result.missingFields.roomId} / 版本 ${result.missingFields.clientVersion}`}
            />
            <SummaryCard
              label="查询耗时"
              value={`${result.queryStats.queryDurationMs} ms`}
              note={`扫描 ${formatBytes(result.queryStats.bytesScanned)}`}
            />
          </div>

          {result.trendGroups.length > 0 || result.trendSeries.length > 0 ? (
            <HTTPFailureTrend
              baseBucketSeconds={
                result.trendGroupBucketSeconds || result.trendBucketSeconds
              }
              from={result.window.from}
              groups={result.trendGroups}
              legacySeries={result.trendSeries}
              to={result.window.to}
            />
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
            <Distribution
              title="状态码分布"
              items={result.statusDistribution}
            />
            <Distribution title="路由分布" items={result.routeDistribution} />
            <Distribution title="原因分布" items={result.reasonDistribution} />
            <Distribution
              title="客户端版本分布"
              items={result.clientVersionDistribution}
            />
          </div>

          <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
            <h2 className="text-base font-semibold">用户与设备聚类</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              按事件中的 userId + deviceId 聚类；候选设备不代表已确认来源。
            </p>
            <Table
              className="mt-4"
              rowKey={(row) => `${row.user.userId}-${row.device.deviceId}`}
              columns={actorColumns}
              dataSource={result.actors}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              scroll={{ x: 1250 }}
              tableLayout="fixed"
              locale={{ emptyText: '当前时间窗没有匹配事件' }}
            />
          </div>

          <RoomTable rooms={result.rooms} />

          <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-accent" />
              <h2 className="text-base font-semibold">事件明细</h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {isHTTPExceptionQuery && !eventPage
                ? '明细默认不随汇总查询加载，避免首屏对同一时间窗重复扫描。'
                : '服务端按固定查询快照游标分页，避免翻页期间的新日志造成重复或遗漏。'}
              {eventPage?.cursorExpiresAt
                ? ` 当前游标有效至 ${formatDateTime(eventPage.cursorExpiresAt)}。`
                : ''}
            </p>
            {isHTTPExceptionQuery && !eventPage ? (
              <div className="mt-4 rounded-md border border-dashed border-border p-6 text-center">
                <Button
                  disabled={eventPageLoading || result.summary.total === 0}
                  variant="outline"
                  onClick={() =>
                    void loadHTTPEventPage(1, DEFAULT_EVENT_PAGE_SIZE)
                  }
                >
                  {eventPageLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <FileSearch2 className="mr-2 h-4 w-4" />
                  )}
                  加载前 {DEFAULT_EVENT_PAGE_SIZE} 条事件明细
                </Button>
              </div>
            ) : (
              <Table
                className="mt-4"
                rowKey={(row, index) =>
                  `${row.requestId || row.timestamp || 'event'}-${index}`
                }
                columns={eventColumns}
                dataSource={eventPage?.events ?? []}
                loading={eventPageLoading}
                pagination={{
                  current: eventPageNumber,
                  pageSize: eventPageSize,
                  pageSizeOptions: [20, 50, 100],
                  showSizeChanger: true,
                  simple: true,
                  total: result.summary.total,
                  showTotal: (total) => `共 ${total} 条匹配事件`,
                }}
                onChange={(pagination) => {
                  const nextPageSize =
                    pagination.pageSize ?? DEFAULT_EVENT_PAGE_SIZE;
                  const nextPage =
                    nextPageSize === eventPageSize
                      ? (pagination.current ?? 1)
                      : 1;
                  if (
                    nextPage !== eventPageNumber ||
                    nextPageSize !== eventPageSize
                  ) {
                    void loadHTTPEventPage(nextPage, nextPageSize);
                  }
                }}
                scroll={{ x: 1050 }}
                locale={{ emptyText: '当前页没有匹配事件' }}
              />
            )}
          </div>
        </>
      ) : null}

      <Drawer
        title="HTTP 失败事件详情"
        width={720}
        open={Boolean(selectedEvent)}
        onClose={() => setSelectedEvent(null)}
      >
        {selectedEvent ? (
          <div className="space-y-5">
            <Alert
              type="info"
              showIcon
              message="请求与错误字段为事件发生时数据；名称、设备状态和房间状态为当前查询结果。"
            />
            <div className="grid gap-3 md:grid-cols-2">
              <DetailItem
                label="事件时间"
                value={formatDateTime(selectedEvent.timestamp)}
                mono={false}
              />
              <DetailItem
                label="HTTP"
                value={`${selectedEvent.statusCode} ${selectedEvent.method}`}
              />
              <DetailItem label="Route" value={selectedEvent.route} />
              <DetailItem label="URL Path" value={selectedEvent.urlPath} />
              <DetailItem label="Reason" value={selectedEvent.reason} />
              <DetailItem
                label="Error Message"
                value={selectedEvent.errorMessage}
              />
              <DetailItem label="Request ID" value={selectedEvent.requestId} />
              <DetailItem
                label="Request Params"
                value={selectedEvent.requestParamsJson}
              />
              <DetailItem
                label="User"
                value={`${selectedActor?.user.name || '名称未解析'} (${selectedEvent.userId})`}
                mono={false}
              />
              <DetailItem
                label="Device"
                value={`${selectedActor?.device.displayName || selectedEvent.deviceName || '名称未提供'} (${selectedEvent.deviceId})`}
                mono={false}
              />
              <DetailItem
                label="Room"
                value={`${selectedRoom?.name || '名称未解析'} (${selectedEvent.roomId})`}
                mono={false}
              />
              <DetailItem
                label="Client"
                value={[
                  selectedEvent.platform,
                  selectedEvent.arch,
                  selectedEvent.clientVersion,
                ]
                  .filter(Boolean)
                  .join(' / ')}
                mono={false}
              />
              <DetailItem label="Client IP" value={selectedEvent.clientIp} />
              <DetailItem label="User-Agent" value={selectedEvent.userAgent} />
            </div>
            {selectedActor?.candidateDevices.length ? (
              <Alert
                type="warning"
                showIcon
                message="该事件没有 deviceId。候选设备仅用于补充上下文，不能作为触发设备结论。"
              />
            ) : null}
          </div>
        ) : null}
      </Drawer>

      <Drawer
        title="Agent 下钻排查 Prompt"
        width={760}
        open={promptDrawerOpen}
        onClose={() => setPromptDrawerOpen(false)}
        extra={
          <Button
            size="sm"
            disabled={!drilldownPrompt}
            onClick={() => void copyDrilldownPrompt()}
          >
            <ClipboardCopy className="mr-2 h-4 w-4" />
            一键复制
          </Button>
        }
      >
        <Alert
          type="info"
          showIcon
          message="Prompt 默认授权 Agent 使用指定 AWS CLI profile 执行只读 CloudWatch 查询，不包含邮箱、IP 或完整 User-Agent。"
        />
        <textarea
          aria-label="Agent 下钻排查 Prompt"
          className="mt-4 min-h-[620px] w-full resize-y rounded-md border border-border bg-slate-950 p-4 font-mono text-xs leading-5 text-slate-100 outline-none"
          readOnly
          value={drilldownPrompt}
        />
      </Drawer>
      {isHTTPExceptionQuery && canRead ? (
        <UserLookupWidget includeSensitive={includeSensitive} />
      ) : null}
    </div>
  );
}

function RoomTable({ rooms }: { rooms: ResolvedRoom[] }) {
  const columns: ColumnsType<ResolvedRoom> = [
    { title: '次数', dataIndex: 'count', width: 80 },
    {
      title: '当前名称',
      dataIndex: 'name',
      render: (value: string) => value || '名称未解析',
    },
    {
      title: 'Room ID',
      dataIndex: 'roomId',
      render: (value: string) => (
        <span className="break-all font-mono text-xs">{value}</span>
      ),
    },
    {
      title: 'Owner',
      dataIndex: 'ownerUserId',
      render: (value: string) => (
        <span className="break-all font-mono text-xs">{value || '-'}</span>
      ),
    },
    {
      title: '当前状态',
      key: 'status',
      render: (_, row) =>
        row.deleted ? (
          <Tag color="red">已删除</Tag>
        ) : (
          resolveLabel(row.resolveStatus)
        ),
    },
  ];
  return (
    <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
      <h2 className="text-base font-semibold">房间归因</h2>
      <Table
        className="mt-4"
        rowKey="roomId"
        columns={columns}
        dataSource={rooms}
        pagination={{ pageSize: 20 }}
      />
    </div>
  );
}
